import { useUiLanguage } from '../../utils/uiTranslation';
import React from 'react';
import axios from 'axios';
import { matchLabel } from './matchLabel';

const hasPending = profiles => profiles.some(p => p.metadata.match_details_pending && p.metadata.match_page);
const cancelled = () => new DOMException('Search cancelled', 'AbortError');

function pause(milliseconds, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) { reject(cancelled()); return; }
    const abort = () => { clearTimeout(timer); reject(cancelled()); };
    const timer = setTimeout(() => { signal?.removeEventListener('abort', abort); resolve(); }, milliseconds);
    signal?.addEventListener('abort', abort, { once: true });
  });
}

// Finish a whole fetched page before exposing any of its cards. Repeated polls
// reuse the backend's existing lookups and assessments; they do not pay again.
async function prepareProfiles(profiles, apiBase, { signal } = {}) {
  if (signal?.aborted) throw cancelled();
  const tokens = [...new Set(profiles.filter(p => p.metadata.match_details_pending).map(p => p.metadata.match_page).filter(Boolean))];
  let ready = profiles;
  await Promise.all(tokens.map(async token => {
    let failures = 0;
    for (let attempt = 0; attempt < 100; attempt += 1) {
      if (signal?.aborted) throw cancelled();
      let data;
      try {
        ({ data } = await axios.post(`${apiBase}/v2/search_matches/${token}`, {}, { signal }));
        failures = 0;
      } catch (error) {
        if (signal?.aborted) throw cancelled();
        if (++failures >= 5) throw new Error('Could not finish checking matches. Please try again.');
        await pause(3000, signal);
        continue;
      }
      if (signal?.aborted) throw cancelled();
      const rows = new Map((data.profiles || []).map(row => [row.id, row]));
      const complete = !data.pending && ready.filter(p => p.metadata.match_page === token)
        .every(p => rows.has(p.metadata.pinecone_id) && !rows.get(p.metadata.pinecone_id).pending);
      // Merge by ID, never by response position. No sorting or partial rendering.
      ready = ready.map(profile => {
        if (profile.metadata.match_page !== token) return profile;
        const row = rows.get(profile.metadata.pinecone_id);
        const matches = row?.pending && !row.matches?.length ? profile.metadata.query_matches : row?.matches;
        return { ...profile, metadata: { ...profile.metadata, ...row?.metadata,
          full_cv: { ...profile.metadata.full_cv, ...row?.metadata?.full_cv },
          query_matches: matches || profile.metadata.query_matches || [],
          match_details_pending: !complete } };
      });
      if (complete) return;
      await pause(2000, signal);
    }
    throw new Error('Could not finish checking matches. Please try again.');
  }));
  return ready;
}

function Badges({ metadata, t }) {
  useUiLanguage();
  // Only explicit yes answers become tags, including when restoring an old search.
  const supported = (metadata.query_matches || []).filter(m => m.matched === true);
  const nationalityTag = metadata?.nationality_tag;
  if (!supported.length && !nationalityTag && !metadata.match_details_pending) return null;
  return (
    <div style={{ marginTop: 14, fontSize: 12, lineHeight: 1.5, textAlign: 'start' }} onClick={event => event.stopPropagation()}>
      <div dir={supported.some(match => match.display_language === 'ar') ? 'rtl' : undefined}
        style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {nationalityTag && (
          <span
            title={nationalityTag}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 9px', borderRadius: 12,
              maxWidth: '100%', boxSizing: 'border-box', color: 'var(--app-text, #fff)', background: 'rgba(34,197,94,.80)',
              border: '1px solid rgba(34,197,94,.30)' }}
          >
            <i className="fa-solid fa-check" aria-hidden="true" style={{ fontSize: 10, flexShrink: 0 }} />
            <bdi dir="auto" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{nationalityTag}</bdi>
          </span>
        )}
        {supported.map(match => <span key={match.id} title={matchLabel(match)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 9px', borderRadius: 12,
            maxWidth: '100%', boxSizing: 'border-box', color: 'var(--app-text, #fff)', background: 'rgba(34,197,94,.20)',
            border: '1px solid rgba(34,197,94,.05)' }}>
          <i className="fa-solid fa-check" aria-hidden="true" style={{ fontSize: 10, flexShrink: 0 }} />
          <bdi dir="auto" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{matchLabel(match)}</bdi>
        </span>)}
      </div>
      {metadata.match_details_pending && <div role="status" style={{ marginTop: 6, opacity: .7 }}>
        {t('results.matches.loading', 'Matching requirements…')}
      </div>}
    </div>
  );
}

export default { Badges, prepareProfiles, hasPending };
